import React, { useEffect, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import JobApplicationTable, { COLUMNS_CONSTANTS as JOB_APPLICATION_COLUMNS } from "./job_application/JobApplicationTable";
import sh from "bundles/common/utils/sh";
import { useSnackbar } from "notistack";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import ApplyToOfferDialog from "bundles/Internal/components/common/ApplyToOfferDialog";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import exportCsv from "bundles/common/utils/exportCsv";
import {
  EMPTY_JOB_APPLICATION_FILTERS,
} from "bundles/Internal/components/job_application/JobApplicationTableFilters";

const JOB_APPLICATION_CREATED_EVENT = "job-application:created";

function buildSearchFilters(activeFilters) {
  const filters = {};
  let hasAny = false;

  if (activeFilters.status?.length) {
    filters.status = activeFilters.status;
    hasAny = true;
  }

  if (activeFilters.source?.length) {
    filters.source = activeFilters.source;
    hasAny = true;
  }

  if (activeFilters.utm_source?.length) {
    filters.utm_source = activeFilters.utm_source;
    hasAny = true;
  }

  if (activeFilters.created_by?.length) {
    filters.creator_id = activeFilters.created_by;
    hasAny = true;
  }

  return hasAny ? filters : null;
}

const JobApplicationsIndexPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobApplications, setJobApplications] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesJobApplication, setNotesJobApplication] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState(EMPTY_JOB_APPLICATION_FILTERS);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editJobApplication, setEditJobApplication] = useState(null);
  const [deleteJobApplication, setDeleteJobApplication] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creators, setCreators] = useState([]);

  useEffect(() => {
    sh.get("/internal/creators", { params: { type: "JobApplication" } })
      .then((res) => setCreators(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  const fetchJobApplications = useCallback((query = "", pageNum = 1, limit = 25, activeFilters = {}) => {
    const hasQuery = Boolean(String(query || "").trim());
    const searchFilters = buildSearchFilters(activeFilters);
    const hasFilters = searchFilters !== null;

    setLoading(true);
    sh.get("/internal/search/job_applications", {
      params: {
        q: hasQuery ? query : undefined,
        page: pageNum,
        limit,
        ...(hasFilters && { filters: JSON.stringify(searchFilters) }),
      },
    })
      .then((res) => {
        const payload = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setJobApplications(payload);
        if (res.data?.pagy) {
          setTotalCount(res.data.pagy.count || payload.length);
        } else {
          setTotalCount(payload.length);
        }
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch job applications", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchJobApplications(searchQuery, 1, rowsPerPage, filters);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchJobApplications, searchQuery, rowsPerPage, filters]);

  useEffect(() => {
    const handler = () => fetchJobApplications(searchQuery, page, rowsPerPage, filters);
    window.addEventListener(JOB_APPLICATION_CREATED_EVENT, handler);
    return () => window.removeEventListener(JOB_APPLICATION_CREATED_EVENT, handler);
  }, [fetchJobApplications, searchQuery, page, rowsPerPage, filters]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchJobApplications(searchQuery, newPage, rowsPerPage, filters);
  }, [fetchJobApplications, searchQuery, rowsPerPage, filters]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleExportCsv = useCallback((ids) => {
    exportCsv("job_applications", ids, authenticity_token, JOB_APPLICATION_COLUMNS)
      .then(() => enqueueSnackbar("CSV exported", { variant: "success" }))
      .catch(() => enqueueSnackbar("Failed to export CSV", { variant: "error" }));
  }, [authenticity_token, enqueueSnackbar]);

  const handleDelete = useCallback(() => {
    if (!deleteJobApplication?.id) return;
    setDeleting(true);
    sh.delete(`/internal/job_applications/${deleteJobApplication.id}`, {
      headers: { "X-CSRF-Token": authenticity_token },
    })
      .then(() => {
        enqueueSnackbar("Job application archived", { variant: "success" });
        setDeleteJobApplication(null);
        fetchJobApplications(searchQuery, page, rowsPerPage, filters);
      })
      .catch(() => enqueueSnackbar("Failed to archive job application", { variant: "error" }))
      .finally(() => setDeleting(false));
  }, [deleteJobApplication, authenticity_token, enqueueSnackbar, fetchJobApplications, searchQuery, page, rowsPerPage, filters]);

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <JobApplicationTable
        jobApplications={jobApplications}
        onQueryChange={(q) => setSearchQuery(q)}
        filters={filters}
        onFiltersChange={setFilters}
        creators={creators}
        onJobApplicationSelect={(jobApplication) => {
          if (!jobApplication?.id) return;
          navigate(`/internal/job_applications/${jobApplication.id}`, {
            state: { jobApplication, from: location.pathname },
          });
        }}
        onOpenNotes={(jobApplication) => {
          setNotesJobApplication(jobApplication);
          setNotesOpen(true);
        }}
        onAddClick={() => setCreateDialogOpen(true)}
        onEdit={(jobApplication) => setEditJobApplication(jobApplication)}
        onDelete={(jobApplication) => setDeleteJobApplication(jobApplication)}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectable
        selectedRows={selectedRows}
        onRowSelect={setSelectedRows}
        onExportCsv={handleExportCsv}
      />
      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="job_applications"
        record={notesJobApplication}
        onNoteAdded={(jobApplicationId) => {
          setJobApplications((prev) =>
            prev.map((ja) =>
              String(ja?.id) === String(jobApplicationId)
                ? { ...ja, notes_count: (Number(ja.notes_count) || 0) + 1 }
                : ja
            )
          );
        }}
        onNoteArchived={(jobApplicationId) => {
          setJobApplications((prev) =>
            prev.map((ja) =>
              String(ja?.id) === String(jobApplicationId)
                ? { ...ja, notes_count: Math.max((Number(ja.notes_count) || 0) - 1, 0) }
                : ja
            )
          );
        }}
      />
      <ApplyToOfferDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onApplied={() => {
          setCreateDialogOpen(false);
          fetchJobApplications(searchQuery, page, rowsPerPage, filters);
        }}
      />
      {editJobApplication && (
        <ApplyToOfferDialog
          open={Boolean(editJobApplication)}
          onClose={() => setEditJobApplication(null)}
          jobApplication={editJobApplication}
          onApplied={() => {
            setEditJobApplication(null);
            fetchJobApplications(searchQuery, page, rowsPerPage, filters);
          }}
        />
      )}
      <ConfirmDialog
        open={Boolean(deleteJobApplication)}
        onClose={() => setDeleteJobApplication(null)}
        onConfirm={handleDelete}
        title="Archive Job Application"
        description="Are you sure you want to archive this job application? It will no longer appear in the platform."
        cancelLabel="Cancel"
        confirmLabel={deleting ? "Archiving…" : "Archive"}
        destructive
      />
    </Box>
  );
};

export default JobApplicationsIndexPage;
